import styled from "@emotion/styled";
import { useConnection } from "@solana/wallet-adapter-react";
import { NATIVE_SOL, STEP_MINT } from "@stepfinance/common";
import { ContextButton, formatPercentage, IconTextContainer, SPACE_MONO, white } from "@stepfinance/react";
import { Select } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { validatorStake } from "../../actions/validator/stake";
import { ReactComponent as StakeIcon } from "../../assets/location.svg";
import { useAccountsContextRefresher } from "../../contexts/accounts";
import { useLanguageContext } from "../../contexts/language/language";
import { useStakingContext } from "../../contexts/staking";
import { useAccountByMint, useConnectedWallet } from "../../hooks";
import { handlePreSendTxnError } from "../../utils/transactions";
import { AmountInput } from "../AmountInput/AmountInput";
import type { ModalProps } from "../Modal/Modal";
import { Modal } from "../Modal/Modal";
import { TokenIcon } from "../TokenIcon";
import type { StakeAction } from "./StakingTabs";

interface ValidatorStakingProps extends Omit<ModalProps, "title" | "children" | "TitleIcon"> {
  stakeAction: StakeAction;
}

const { Option } = Select;

export const ValidatorStakingModal: FC<ValidatorStakingProps> = ({ stakeAction: _, onClose, width, visible }) => {
  const [stakeAmount, setStakeAmount] = useState("");
  const [hasSufficientBalance, setHasSufficientBalance] = useState<boolean>(true);
  const { connection } = useConnection();
  const refreshAccounts = useAccountsContextRefresher();
  const wallet = useConnectedWallet();
  const userSOLAccount = useAccountByMint(NATIVE_SOL);
  const userBalance = userSOLAccount ? userSOLAccount.tokenAmount.uiAmount : 0;
  const [transactionPending, setTransactionPending] = useState(false);
  const disableSend = !hasSufficientBalance || transactionPending || !stakeAmount;
  const {
    currentLanguageData: {
      common,
      components: { validatorStakingModal },
    },
  } = useLanguageContext();

  const { solStakingAPYMap } = useStakingContext();

  useEffect(() => {
    if (stakeAmount && Number(stakeAmount) > userBalance) {
      setHasSufficientBalance(false);
    } else {
      setHasSufficientBalance(true);
    }
  }, [stakeAmount, userBalance]);

  useEffect(() => {
    setStakeAmount("");
  }, [userSOLAccount]);

  const handleStake = () => {
    if (stakeAmount && wallet && userBalance) {
      setTransactionPending(true);
      validatorStake(wallet, connection, Number(stakeAmount), setTransactionPending, {
        ...common.notifications,
        ...validatorStakingModal.notifications,
      })
        .catch((e) => {
          handlePreSendTxnError(e as Error, "Error staking funds");
          onClose();
        })
        .finally(() => {
          setTransactionPending(false);
          refreshAccounts();
          onClose();
        });
    }
  };

  return (
    <Modal TitleIcon={StakeIcon} title={common.stakeStepSOL} onClose={onClose} width={width} visible={visible}>
      <ContentContainer>
        <AmountInput
          headerText={validatorStakingModal.youWillStake}
          amount={stakeAmount}
          mint={NATIVE_SOL}
          mintDecimals={9}
          onAmountChange={(_event, amount) => {
            setStakeAmount(amount);
          }}
        />
        <div className="select-platform-container">
          <span className="select-platform-info">{validatorStakingModal.using}</span>
          <Select className="select-platform" value={"Step Finance"} showArrow={false} disabled>
            <Option value={"Step Finance"}>
              <div className="option-container">
                <IconTextContainer>
                  <TokenIcon size={24} mintAddress={STEP_MINT.toBase58()} />
                  <span className="platform-name">{validatorStakingModal.stepValidator}</span>
                </IconTextContainer>
                <span className="platform-apy">
                  {formatPercentage(solStakingAPYMap ? solStakingAPYMap["step"] : 0)} APY
                </span>
              </div>
            </Option>
          </Select>
        </div>
        <div className="buttons-container">
          <ContextButton onClick={onClose} className="cancel-button" borderless>
            {common.cancelButton}
          </ContextButton>
          <ContextButton onClick={handleStake} disabled={disableSend}>
            {!stakeAmount
              ? validatorStakingModal.enterAnAmount
              : hasSufficientBalance
              ? validatorStakingModal.stake
              : validatorStakingModal.insufficientFunds}
          </ContextButton>
        </div>
      </ContentContainer>
    </Modal>
  );
};

const ContentContainer = styled.div`
  margin-top: 12px;
  .buttons-container {
    margin-top: 32px;
    display: flex;
    justify-content: flex-end;
    .cancel-button {
      margin-right: 8px;
    }
  }
  .select-platform-container {
    margin: 16px 0;
    .select-platform-info {
      color: ${white};
    }
    .select-platform {
      margin-top: 16px;
      width: 100%;
      .option-container {
        display: flex;
        justify-content: space-between;
        height: 100%;
        align-items: center;
        .platform-name {
          font-size: 14px;
          margin-left: 4px;
        }
        .platform-apy {
          font-family: ${SPACE_MONO};
        }
      }
    }
  }
`;
